import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { rem } from '@utils';

const Wrapper = styled.div<{ disabled?: boolean }>(
  ({ disabled, theme }) => `
  background: ${theme.palette.common.white};
  border-radius: 4px;
  color: ${theme.palette.common.black};
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  max-width: var(--prompt-max-width, 100%);
  margin: 0 auto;
  padding: ${rem(12)} ${rem(16)};
  position: relative;
  transition: box-shadow 100ms ease 10ms;
  z-index: 2;

  ${
    disabled
      ? `
    box-shadow: inset 0 0 0 1px ${theme.palette.grey['200']};
    cursor: wait;

    * {
      cursor: wait;
      pointer-events: none;
    }

    ${StopButton} * {
      cursor: pointer;
      pointer-events: auto;
    }

    .button {
      border: none;
      color: ${theme.palette.grey['500']};
    }
  `
      : `
    box-shadow: inset 0 0 0 1px ${theme.palette.common.black};
    cursor: text;

    &:hover {
      box-shadow: inset 0 0 0 1px ${theme.palette.primary.main};

      ${SendButton}, ${StopButton} {
        color: ${theme.palette.primary.main};
      }
    }
      
    &:focus-within {
      box-shadow: inset 0 0 0 2px ${theme.palette.primary.dark};

      ${SendButton}, ${StopButton} {
        color: ${theme.palette.primary.dark};
      }
    }    
    `
  };

  .dataset-picker {
    height: auto;
    max-height: var(--dataset-picker-max-height);
    // Avoid overriding original scrollbar styles 
    // with those from the .overlay-scrollbar class
    scrollbar-color: auto;
  }

  @media (pointer: coarse) {
    .dataset-picker {
      max-height: 45dvh;
    }
  }  
`
);

const ControlsBar = styled.div<{ hidden?: boolean }>(
  ({ hidden, theme, onClick }) => `
  cursor: ${onClick ? 'text' : 'default'};
  display: ${hidden ? 'none' : 'flex'};
  align-items: center;
  justify-content: space-between;
  column-gap: ${rem(4)};
  height: ${rem(32)};
  position: relative;
  width: 100%;

  .button {
    flex: 0 0 auto;
  }

  && .button.selected {
    background: ${theme.palette.primary.dark};
    border-color: ${theme.palette.primary.dark};
    color: ${theme.palette.common.white};
  }

  && .button * {
    transition: none;
  }
`
);

const TextAreaWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TextArea = styled.textarea(
  ({ theme }) => `
  background: ${theme.palette.common.white};
  border: none;
  color: ${theme.palette.common.black};
  font-size: ${rem(14)};
  font-family: inherit;
  font-weight: 500;
  line-height: ${rem(24)};
  min-height: ${rem(24)};
  resize: none;
  width: 100%;
  
  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${theme.palette.grey['600']};
    font-weight: 400;
    opacity: 1;
    padding: 0.5px 0 0 0.5px;
  }

  &:placeholder-shown {
    overflow: hidden;
  }

  @media (pointer: coarse) {
    font-size: ${rem(16)};

    &::placeholder {
      padding: 0;
    }
  }
`
);

const Badge = styled.div<{ disabled?: boolean }>(
  ({ disabled, theme }) => `
  background: ${theme.palette.common.black};
  border-radius: 4px;
  color: ${theme.palette.common.white};
  cursor: default;
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  flex-grow: 0;
  column-gap: ${rem(8)};
  font-weight: 500;
  padding: ${rem(8)};
  position: relative;
  user-select: none;
  ${
    disabled &&
    `
      cursor: not-allowed;
      opacity: 0.5;
      pointer-events: none;
    `
  };

  > * {
    pointer-events: none;
  }
  `
);

const Menu = styled.menu<{ hidden?: boolean }>(
  ({ hidden, theme }) => `
  background: ${theme.palette.common.white};
  border-radius: 4px;
  box-shadow: 0px 2px 14px 0px rgba(0, 0, 0, 0.08);
  cursor: default;
  display: flex;
  flex-flow: column;
  overflow-x: hidden;
  max-height: var(--prompt-menu-max-height);
  max-width: var(--audience-menu-max-width);
  opacity: ${hidden ? 0 : 1};
  padding: ${rem(4)} 0;
  position: absolute;
  left: 0;
  pointer-events: ${hidden ? 'none' : 'auto'};
  transition: 
    opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) ${hidden ? 0 : 200}ms;
  width: 100vw;
  will-change: opacity;
  z-index: ${hidden ? -1 : 3};

  // Only on non-touch devices
  @media (hover: hover) and (pointer: fine) {
    opacity: 0;
    transition: opacity 200ms ease, visibility 0ms 200ms;
    visibility: hidden;

    ${Badge}:hover & {
      opacity: 1;
      visibility: visible;
    }
  }

  @media (max-width: 430px) {
    max-width: calc(100vw - 4 * ${rem(16)});
  }
`
);

const MenuLabel = styled.div`
  color: ${({ theme }) => theme.palette.grey['600']};
  font-size: ${rem(14)};
  font-weight: 500;
  margin: ${rem(16)} 0;
  padding: 0 ${rem(16)};
`;

const List = styled.ul<{ alwaysShowScrollbar?: boolean; scrollable?: boolean }>`
  margin-right: ${({ scrollable }) => rem(scrollable ? 4 : 0)};
  overflow-y: ${({ alwaysShowScrollbar, scrollable }) => {
    if (scrollable) {
      if (alwaysShowScrollbar) {
        return 'visible';
      }

      return 'auto';
    }

    return 'hidden';
  }};
  overflow-x: hidden;

  &&::-webkit-scrollbar {
    width: ${rem(8)};
  }

  &&::-webkit-scrollbar-thumb {
    border-width: ${rem(2)};
  }
`;

const ListItem = styled.li<{ selected?: boolean }>(
  ({ selected, theme }) => `
  color: ${theme.palette.common.black};
  cursor: ${selected ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  flex-flow: row nowrap;
  gap: ${rem(12)};
  padding: ${rem(8)} ${rem(16)};
  user-select: none;

  .action-icon {
    color: ${theme.palette.grey['600']};
    flex-shrink: 0;
    margin-right: ${rem(-4)};
    width: ${rem(16)};
    height: ${rem(16)};
    transition: color 100ms ease;
  }

  .icon {
    flex-shrink: 0;
  }

  *:is(${ListItemIcon}, ${ListItemTitle}, ${ListItemSubtitle}) {
    transition: color 100ms ease;
  }

  *:is(${ListItemIcon}, ${ListItemTitle}) {
    color: ${selected ? theme.palette.primary.dark : 'inherit'};
  }

  &:hover *:is(${ListItemIcon}, ${ListItemTitle}, ${ListItemSubtitle}) {
    ${!selected && `color: ${theme.palette.primary.main};`}
  }

  &:hover .action-icon {
    color: ${theme.palette.primary.main};
  }

  &:focus-visible {
    outline: 2px solid ${theme.palette.primary.dark};
    outline-offset: -2px;
  }

  @media (max-width: 430px) {
    .action-icon {
      border-radius: 4px;
      box-shadow: inset 0 0 0 1px ${theme.palette.grey['500']};
      box-sizing: content-box;
      color: ${theme.palette.grey['500']};
      height: ${rem(14)};
      padding: ${rem(8)};
      width: ${rem(14)};
    }

    &:hover .action-icon {
      box-shadow: inset 0 0 0 1px ${theme.palette.primary.main};
    }
  }
`
);

const ListItemIcon = styled.div`
  flex-shrink: 0;
`;

const ListItemTitle = styled.div`
  font-weight: 600;

  ${Menu}.dataset-menu & {
    grid-area: 1 / 2;
  }
`;

const ListItemSubtitle = styled.div`
  color: ${({ theme }) => theme.palette.grey['500']};
`;

const ListItemContent = styled.div`
  flex-grow: 1;
  font-size: ${rem(12)};
  line-height: ${rem(16)};
  overflow: hidden;
  white-space: nowrap;

  * {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const SelectedItems = styled.div<{
  hidden?: boolean;
  overflow?: 'auto' | 'hidden' | 'visible';
}>(
  ({ hidden, overflow, theme }) => `
  display: ${hidden ? 'none' : 'flex'};
  flex-wrap: nowrap;
  gap: ${rem(8)};
  overflow: ${overflow};
  
  // Enable text-overflow for pill elements
  .pill-tooltip, 
  .pill-root, 
  .pill-icons-and-text, 
  .pill-label {
    overflow: hidden;
  }

  .pill-tooltip {
    display: ${hidden ? 'none' : 'flex'};
    flex-wrap: nowrap;
    gap: ${rem(8)};
    overflow: ${overflow};
  }

  .pill-root {
    background: none;
  }

  .pill-root.disabled * {
    box-shadow: none;
    color: ${theme.palette.grey['400']};
    fill: ${theme.palette.grey['400']};
    transition: none;
  }

  .pill-label {
    padding-right: 0;
  }

  .pill-icons-and-text {
    margin: 0 ${rem(-8)} 0 ${rem(-4)};
    column-gap: ${rem(8)};
  }

  .pill-end-icon {
    flex-shrink: 0;
    width: ${rem(24)};
    height: ${rem(24)};
  }
`
);

const IconWrapper = styled.div(
  ({ theme }) => `
  border-radius: 50%;
  color: ${theme.palette.common.white};
  cursor: pointer;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: ${rem(4)};
  transition: background 150ms ease;

  .icon {
    width: ${rem(14)};
  }

  &:hover {
    background: ${theme.palette.primary.dark};
  }
`
);

const Button = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  scale: 1;
  transition: scale 100ms linear;
  user-select: none;

  &:focus-visible {
    border-radius: 50%;
    outline: 2px solid ${({ theme }) => theme.palette.primary.dark};
    outline-offset: 1px;
  }

  &:active {
    scale: 0.8;
  }  
`;

const SendButton = styled(Button)`
  box-sizing: content-box;
  color: inherit;
  height: ${rem(16)};
  width: ${rem(16)};
  padding: ${rem(8)};
`;

const StopButton = styled(Button)`
  color: ${({ theme }) => theme.palette.grey['600']};
  height: ${rem(32)};
  width: ${rem(32)};
  padding: ${rem(4)};
  pointer-events: auto;

  .tooltip-wrapper {
    height: ${rem(24)};
    width: ${rem(24)};
  }
  
  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const ellipsis = keyframes`
  0% { 
    content: '';
    opacity: 0.25;
  }
  25% { 
    content: '.';
    opacity: 0.5;
  }
  50% { 
    content: '..';
    opacity: 0.75;
  }
  75% { 
    content: '...';
    opacity: 1;
  }
  100% { 
    content: '';
    opacity: 0.25;
  }
`;

const Loading = styled.span`
  color: ${({ theme }) => theme.palette.grey['500']};
  display: flex;
  align-items: center;
  height: ${rem(24)};
  
  &::after {
    content: '';
    animation: ${ellipsis} 1.2s linear infinite;
  }
`;

const PillTooltipWrapper = styled.div`
  max-width: 288px;
  padding: ${rem(20)} ${rem(16)};
`;

const PillTooltipHeader = styled.h5`
  color: ${({ theme }) => theme.palette.common.white};
  font-size: ${rem(20)};
  font-weight: 600;
  line-height: ${rem(32)};
  margin-bottom: ${rem(8)};
`;

const PillTooltipContent = styled.p`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(18)};
  margin-bottom: ${rem(24)};
`;

export default {
  ControlsBar,
  Badge,
  IconWrapper,
  TextArea,
  TextAreaWrapper,
  List,
  ListItem,
  ListItemContent,
  ListItemIcon,
  ListItemTitle,
  ListItemSubtitle,
  Loading,
  Menu,
  MenuLabel,
  PillTooltipWrapper,
  PillTooltipHeader,
  PillTooltipContent,
  SendButton,
  StopButton,
  SelectedItems,
  Wrapper
};
